import type React from "react";
import { eventEmitter } from "../../../utils/eventEmitter";

// Mirrors the backend payload from
// agent_runtime/surfaces/superdoc.py:_handle_artifact_updated_event.
export interface ArtifactUpdatedData {
  audio_file_id: number;
  kinds: string[];
  // True when this is a freshly created note VERSION still regenerating
  // in the background — the panel opens it automatically when done.
  generating?: boolean;
  parent_audio_file_id?: number;
}

const KIND_LABELS: Record<string, string> = {
  note: "Note",
  session_insights: "Session insights",
  treatment_plan: "Treatment plan",
  procedural_codes: "CPT codes",
  diagnostic_codes: "Diagnosis codes",
  progress_summary: "Progress summary",
};

const DocumentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M4 1.5H9.5L12.5 4.5V13C12.5 13.55 12.05 14 11.5 14H4C3.45 14 3 13.55 3 13V2.5C3 1.95 3.45 1.5 4 1.5Z"
      stroke="#6366F1"
      strokeWidth="1.3"
      fill="#EEF2FF"
    />
    <path d="M5.5 7.5H10M5.5 10H10" stroke="#6366F1" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const ArtifactUpdatedWidget: React.FC<{ data: ArtifactUpdatedData }> = ({ data }) => {
  if (!data || !data.audio_file_id) return null;

  if (data.generating) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <DocumentIcon />
          <span style={styles.headerText}>New note version generating — it will open automatically when ready</span>
        </div>
      </div>
    );
  }

  const labels = (data.kinds || []).map((k) => KIND_LABELS[k] || k);
  const label = labels.length > 0 ? labels.join(" + ") : "Note";
  const title = data.parent_audio_file_id ? `New note version — ${label.toLowerCase()} updated` : `${label} updated`;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <DocumentIcon />
        <span style={styles.headerText}>{title}</span>
        <button type="button" style={styles.viewButton} onClick={() => eventEmitter.emit("artifactUpdated", data)}>
          View
        </button>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 14px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#F5F3FF",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  headerText: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#111827",
  },
  viewButton: {
    marginLeft: "auto",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "8px",
    border: "1px solid #C7D2FE",
    backgroundColor: "#FFFFFF",
    color: "#4338CA",
    cursor: "pointer",
    fontFamily: "inherit",
  },
};

export default ArtifactUpdatedWidget;
